import { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, Smartphone, Mail, Loader2, KeyRound, Copy, Laptop, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const INP = "bg-[#0b0811] border-white/10 text-white h-10";

export function BackupCodes({ codes }) {
  const copy = () => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Codes copiés"); };
  return (
    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4" data-testid="twofa-backup-codes">
      <div className="text-xs font-bold text-amber-300 mb-2">Codes de secours — conservez-les en lieu sûr, ils ne seront plus affichés</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 font-mono text-sm">
        {codes.map(c => <div key={c} className="bg-black/30 rounded px-2 py-1 text-center">{c}</div>)}
      </div>
      <Button size="sm" variant="outline" onClick={copy} className="mt-3 border-white/10 text-slate-300 text-xs" data-testid="twofa-copy-backup"><Copy className="w-3 h-3 mr-1" /> Copier</Button>
    </div>
  );
}

// Enrôlement : choix méthode → QR / e-mail → code → codes de secours
export function TwoFactorEnroll({ api, onEnabled, forced = false }) {
  const [method, setMethod] = useState("totp");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [backup, setBackup] = useState(null);

  const start = async (m) => {
    setBusy(true); setError(""); setMethod(m);
    try { const r = await api.post("/auth/2fa/setup", { method: m }); setSetup(r.data); }
    catch (e) { setError(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  const enable = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const r = await api.post("/auth/2fa/enable", { code: code.trim() });
      setBackup(r.data.backup_codes); toast.success("Double authentification activée");
      onEnabled?.(r.data);
    } catch (err) { setError(err.response?.data?.detail || "Code incorrect"); }
    setBusy(false);
  };

  if (backup) return <BackupCodes codes={backup} />;

  if (!setup) {
    return (
      <div className="space-y-3" data-testid="twofa-enroll">
        {forced && <p className="text-sm text-amber-300">Votre rôle exige la double authentification. Choisissez une méthode pour continuer.</p>}
        <div className="grid sm:grid-cols-2 gap-3">
          <button onClick={() => start("totp")} disabled={busy} className="text-left bg-[#0b0811] border border-white/10 hover:border-[#f7c935]/50 rounded-xl p-4 transition-colors" data-testid="twofa-method-totp">
            <Smartphone className="w-5 h-5 text-[#f7c935] mb-2" />
            <div className="font-bold text-sm">Application d'authentification</div>
            <div className="text-xs text-slate-500 mt-1">Google Authenticator, Microsoft Authenticator, Authy… Recommandé.</div>
          </button>
          <button onClick={() => start("email")} disabled={busy} className="text-left bg-[#0b0811] border border-white/10 hover:border-[#f7c935]/50 rounded-xl p-4 transition-colors" data-testid="twofa-method-email">
            <Mail className="w-5 h-5 text-sky-400 mb-2" />
            <div className="font-bold text-sm">Code par e-mail</div>
            <div className="text-xs text-slate-500 mt-1">Un code à 6 chiffres envoyé à chaque connexion.</div>
          </button>
        </div>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
        {error && <div className="text-xs text-red-400">{error}</div>}
      </div>
    );
  }

  return (
    <form onSubmit={enable} className="space-y-3" data-testid="twofa-enroll-confirm">
      {method === "totp" ? (
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          {setup.qr_png_base64 && <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="QR code" className="w-40 h-40 rounded-lg bg-white p-1" data-testid="twofa-qr" />}
          <div className="text-xs text-slate-400 space-y-2">
            <p>1. Ouvrez votre application d'authentification et scannez ce QR code.</p>
            <p>2. Ou saisissez la clé manuellement : <code className="bg-black/30 px-1.5 py-0.5 rounded text-[#f7c935] break-all" data-testid="twofa-secret">{setup.secret}</code></p>
            <p>3. Entrez le code à 6 chiffres affiché pour confirmer.</p>
          </div>
        </div>
      ) : (
        <div className="text-xs text-slate-400">
          {setup.email_sent ? `Un code a été envoyé à ${setup.email_hint}.` : <span className="text-amber-400" data-testid="twofa-setup-dev-code">SMTP non configuré — code de test : <b>{setup.dev_code}</b></span>}
        </div>
      )}
      <div className="flex gap-2">
        <Input value={code} onChange={e => setCode(e.target.value)} placeholder="Code à 6 chiffres" className={INP + " font-mono tracking-widest max-w-[200px]"} autoComplete="one-time-code" data-testid="twofa-enable-code" />
        <Button type="submit" disabled={busy || code.trim().length < 6} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="twofa-enable-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />} Activer
        </Button>
        <Button type="button" variant="ghost" onClick={() => setSetup(null)} className="text-slate-400 text-xs">Changer de méthode</Button>
      </div>
      {error && <div className="text-xs text-red-400" data-testid="twofa-enable-error">{error}</div>}
    </form>
  );
}

export default function TwoFactorCard() {
  const { axiosAuth, checkAuth } = useAuth();
  const api = axiosAuth();
  const [status, setStatus] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [newBackup, setNewBackup] = useState(null);

  const load = () => api.get("/auth/2fa/status").then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const disable = async () => {
    if (!pw) { toast.error("Saisissez votre mot de passe"); return; }
    setBusy(true);
    try { await api.post("/auth/2fa/disable", { password: pw }); toast.success("Double authentification désactivée"); setPw(""); await load(); checkAuth?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  const regen = async () => {
    if (!pw) { toast.error("Saisissez votre mot de passe"); return; }
    setBusy(true);
    try { const r = await api.post("/auth/2fa/backup-codes", { password: pw }); setNewBackup(r.data.backup_codes); setPw(""); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  const forget = async () => {
    try { const r = await api.delete("/auth/2fa/trusted-devices"); toast.success(`${r.data.removed} appareil(s) oublié(s)`); await load(); }
    catch (e) { toast.error("Erreur"); }
  };

  if (!status) return null;
  return (
    <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 mt-6" data-testid="twofa-card">
      <div className="flex items-center gap-2 mb-1">
        {status.enabled ? <ShieldCheck className="w-4 h-4 text-green-400" /> : <ShieldOff className="w-4 h-4 text-slate-500" />}
        <h3 className="text-sm font-bold text-white">Double authentification (2FA)</h3>
        <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold ${status.enabled ? "bg-green-500/15 text-green-400" : "bg-white/5 text-slate-400"}`} data-testid="twofa-status-badge">
          {status.enabled ? `Active · ${status.method === "email" ? "e-mail" : "application"}` : "Inactive"}
        </span>
        {status.required && !status.enabled && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 uppercase font-bold">Obligatoire pour votre rôle</span>}
      </div>
      <p className="text-[11px] text-slate-500 mb-4">Un second code (application ou e-mail) est demandé à chaque connexion depuis un nouvel appareil.</p>

      {!status.enabled && !enrolling && <Button onClick={() => setEnrolling(true)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold text-xs" data-testid="twofa-start-btn"><KeyRound className="w-4 h-4 mr-1" /> Activer la double authentification</Button>}
      {!status.enabled && enrolling && <TwoFactorEnroll api={api} onEnabled={() => { load(); checkAuth?.(); }} />}

      {status.enabled && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            <div className="bg-[#0b0811] rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Codes de secours</div><div className="font-bold text-sm" data-testid="twofa-backup-left">{status.backup_codes_left}</div></div>
            <div className="bg-[#0b0811] rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Appareils de confiance</div><div className="font-bold text-sm">{status.trusted_devices}</div></div>
            <div className="bg-[#0b0811] rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Activée le</div><div className="font-bold text-sm">{status.enabled_at?.slice(0, 10)}</div></div>
          </div>
          {newBackup && <BackupCodes codes={newBackup} />}
          <div className="flex flex-wrap items-center gap-2">
            <Input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Mot de passe (confirmation)" className={INP + " max-w-[240px] h-9 text-xs"} data-testid="twofa-password-confirm" />
            <Button size="sm" variant="outline" onClick={regen} disabled={busy} className="border-white/10 text-slate-300 text-xs" data-testid="twofa-regen-btn"><RefreshCw className="w-3 h-3 mr-1" /> Nouveaux codes de secours</Button>
            <Button size="sm" variant="outline" onClick={forget} className="border-white/10 text-slate-300 text-xs" data-testid="twofa-forget-btn"><Laptop className="w-3 h-3 mr-1" /> Oublier les appareils</Button>
            {!status.required && <Button size="sm" variant="outline" onClick={disable} disabled={busy} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" data-testid="twofa-disable-btn"><ShieldOff className="w-3 h-3 mr-1" /> Désactiver</Button>}
          </div>
        </div>
      )}
    </div>
  );
}
